import { useState, useRef, useEffect, useCallback } from 'react';
import {MEDIA, ROLES} from "./Const";
import { v4 as uuidv4 } from 'uuid';
import {Request} from "./request";

const DUPLICATE_CHECK_ROLE_NAMES = [ROLES.ORDINARY.name, ROLES.UM.name];

const shouldCheckDuplicates = () => {
    try {
        const role = localStorage.getItem('ROLE');
        return DUPLICATE_CHECK_ROLE_NAMES.includes(role);
    } catch {
        return false;
    }
};

const useFileUploader = (options = {}) => {
    const {
        compress = true,
        onUploadComplete = () => {},
        onUploadError = () => {},
        onUploadRejected = () => {},
        onProgress = () => {},
        timeout = 300000 // 5 милисекы
/*        timeout = 5000 // 5 милисекы*/
    } = options;

    // Состояния
    const [queue, setQueue] = useState([]);
    const [activeUploadId, setActiveUploadId] = useState(null);
    const [progress, setProgress] = useState({});
    const [statuses, setStatuses] = useState({});
    const [uploadedFiles, setUploadedFiles] = useState([]);

    // Refs
    const abortControllerRef = useRef({});
    const progressTimeoutRef = useRef({});
    const lastProgressTimeRef = useRef({});

    const processQueueRef = useRef(null);
    const uploadFileRef = useRef(null);

    // Фунукция для блокировки прикрепляния поторно загруженных файлов которые были загружены ранее
    const handleUploadRejected = useCallback((fileId, reason) => {
        clearTimeout(progressTimeoutRef.current[fileId]);
        delete progressTimeoutRef.current[fileId];

        // Удаляем файл из обоих списков
        setUploadedFiles(prev => {
            const fileToRemove = prev.find(f => f.id === fileId);
            if (fileToRemove?.previewUrl && fileToRemove.file) {
                URL.revokeObjectURL(fileToRemove.previewUrl);
            }
            return prev.filter(f => f.id !== fileId);
        });
        setQueue(prev => prev.filter(f => f.id !== fileId));

        setStatuses(prev => {
            const updated = { ...prev };
            delete updated[fileId];
            return updated;
        });
        setProgress(prev => {
            const updated = { ...prev };
            delete updated[fileId];
            return updated;
        });

        delete abortControllerRef.current[fileId];
        delete lastProgressTimeRef.current[fileId];

        onUploadRejected(fileId, reason);

        setActiveUploadId(null);
        processQueueRef.current();
    }, [onUploadRejected]);

    // 1. Функции-обработчики, обернутые в useCallback
    const updateProgress = useCallback((fileId, percent) => {
        lastProgressTimeRef.current[fileId] = Date.now();

        setProgress(prev => ({
            ...prev,
            [fileId]: percent
        }));

        clearTimeout(progressTimeoutRef.current[fileId]);
        setupProgressTimeout(fileId);
    }, []);

    const handleUploadError = useCallback((fileId, error) => {
        clearTimeout(progressTimeoutRef.current[fileId]);
        delete progressTimeoutRef.current[fileId];

        setUploadedFiles(prev =>
            prev.map(file =>
                file.id === fileId
                    ? { ...file, status: 'error', error }
                    : file
            )
        );

        setStatuses(prev => ({
            ...prev,
            [fileId]: 'error'
        }));

        onUploadError(fileId, error);

        delete abortControllerRef.current[fileId];
        delete lastProgressTimeRef.current[fileId];
        setActiveUploadId(null);
        processQueueRef.current();
    }, [onUploadError]);

    const setupProgressTimeout = useCallback((fileId) => {
        progressTimeoutRef.current[fileId] = setTimeout(() => {
            const timeSinceLastProgress = Date.now() - lastProgressTimeRef.current[fileId];
            if (timeSinceLastProgress >= timeout) {
                handleUploadError(fileId, new Error('Ошибка при загрузке'));
                abortControllerRef.current[fileId]?.abort();
            }
        }, timeout);
    }, [timeout, handleUploadError]);

    const setupAbortAndTimeout = useCallback((fileId) => {
        abortControllerRef.current[fileId] = new AbortController();
        lastProgressTimeRef.current[fileId] = Date.now();
        setupProgressTimeout(fileId);
    }, [setupProgressTimeout]);

    const handleUploadSuccess = useCallback((fileId, response) => {
        clearTimeout(progressTimeoutRef.current[fileId]);
        delete progressTimeoutRef.current[fileId];

        setUploadedFiles(prev =>
            prev.map(file =>
                file.id === fileId
                    ? { ...file, status: 'completed', serverId: response.data.id }
                    : file
            )
        );

        setStatuses(prev => ({
            ...prev,
            [fileId]: 'completed'
        }));

        setProgress(prev => ({
            ...prev,
            [fileId]: 100
        }));

        onUploadComplete(fileId, response.data.id);

        delete abortControllerRef.current[fileId];
        delete lastProgressTimeRef.current[fileId];

        setQueue(prev => prev.filter(f => f.id !== fileId));
        setActiveUploadId(null);
        processQueueRef.current();
    }, [onUploadComplete]);


    // 2. Инициализация существующих файлов
    const initializeExistingFiles = useCallback((existingFiles) => {
        const existingFilesFormatted = existingFiles.map(id => {
            const fileId = uuidv4();
            return {
                id: fileId,
                serverId: id,
                name: `existing_file_${id}`,
                previewUrl: `${MEDIA}/api/attachments/${id}`,
                status: 'completed',
                progress: 100
            };
        });

        setUploadedFiles(prev => [...prev, ...existingFilesFormatted]);

        existingFilesFormatted.forEach(file => {
            setStatuses(prev => ({ ...prev, [file.id]: 'completed' }));
            setProgress(prev => ({ ...prev, [file.id]: 100 }));
        });
    }, []);


    // 3. uploadFile (функция, вызываемая внутри useFileUploader)
    const uploadFile = useCallback(async (file) => {
        const fileId = file.id;

        // Проверяем, был ли запрос отменен до начала отправки
        if (abortControllerRef.current[fileId]?.signal.aborted) {
            return;
        }

        const fileToUpload = queue.find(f => f.id === fileId);
        // Добавлена проверка на наличие file.file, так как для существующих файлов его не будет
        if (!fileToUpload || !fileToUpload.file) {
            setQueue(prev => prev.filter(f => f.id !== fileId));
            setActiveUploadId(null);
            processQueueRef.current();
            return;
        }

        setStatuses(prev => ({
            ...prev,
            [fileId]: 'uploading'
        }));
        setUploadedFiles(prev =>
            prev.map(f =>
                f.id === fileId
                    ? { ...f, status: 'uploading' }
                    : f
            )
        );

        const formData = new FormData();
        formData.append('file', fileToUpload.file);

        setupAbortAndTimeout(fileId);

        Request(
            "POST",
            `${MEDIA}/api/attachments?compress=${compress}`,
            null,
            formData,
            (response) => {
                if (response?.data?.is_exist && shouldCheckDuplicates()) {
                    handleUploadRejected(
                        fileId,
                        'Этот файл уже был загружен ранее. Прикрепить его нельзя.'
                    );
                    return;
                }
                handleUploadSuccess(fileId, response);
            },
            (error) => {
                handleUploadError(fileId, error);
            },
            (event) => {
                if (event.lengthComputable) {
                    const percent = ((event.loaded / event.total) * 100).toFixed(2);
                    updateProgress(fileId, percent);
                    onProgress(fileId, percent);
                }
            },
            () => {},
            () => {
            },
            abortControllerRef.current[fileId]
        );
    }, [queue, compress, setupAbortAndTimeout, handleUploadSuccess, handleUploadError, handleUploadRejected, updateProgress, onProgress]);


    // 4. processQueue (оборачиваем в useCallback)
    const processQueue = useCallback(() => {
        if (activeUploadId) return; // Если уже что-то загружается, не запускаем новую загрузку

        // Ищем первый файл, который находится в очереди (pending) или имеет статус 'error' и был помечен для повторной попытки
        const nextFile = queue.find(
            file => (statuses[file.id] === 'pending') ||
                (statuses[file.id] === 'error' && file.isRetrying)
        );

        if (!nextFile) {
            setActiveUploadId(null); // Убедимся, что нет активной загрузки
            return;
        }

        setActiveUploadId(nextFile.id);
        uploadFileRef.current(nextFile);
    }, [queue, activeUploadId, statuses, uploadFile]);

    useEffect(() => {
        processQueueRef.current = processQueue;
        uploadFileRef.current = uploadFile;
    }, [processQueue, uploadFile]);


    // 5. useEffect для инициализации и запуска очереди
    useEffect(() => {
        const hasPending = queue.some(file => statuses[file.id] === 'pending' || (statuses[file.id] === 'error' && file.isRetrying));
        if (hasPending && !activeUploadId) {
            processQueueRef.current();
        }
    }, [queue, activeUploadId, statuses]);


    // 6. Остальные функции, которые возвращаются наружу
    // Добавление файлов в очередь
    const addFiles = useCallback((files) => {
        const newFiles = Array.from(files).map(file => {
            const id = uuidv4();
            return {
                id,
                file,
                name: file.name,
                previewUrl: URL.createObjectURL(file),
                status: 'pending',
                progress: 0,
                isRetrying: false // Добавляем флаг для повторной попытки
            };
        });

        setQueue(prev => [...prev, ...newFiles]);
        setUploadedFiles(prev => [...prev, ...newFiles]);

        const newStatuses = newFiles.reduce((acc, file) => {
            acc[file.id] = 'pending';
            return acc;
        }, {});
        setStatuses(prev => ({ ...prev, ...newStatuses }));

        // Запускаем очередь, если нет активной загрузки
        if (!activeUploadId) {
            processQueueRef.current();
        }
    }, [activeUploadId]);


    // Удаление файла
    const removeFile = useCallback((fileId) => {
        if (activeUploadId === fileId) {
            abortControllerRef.current[fileId]?.abort();
        }

        clearTimeout(progressTimeoutRef.current[fileId]);
        delete progressTimeoutRef.current[fileId];

        setQueue(prev => prev.filter(f => f.id !== fileId));
        setUploadedFiles(prev => {
            const updated = prev.filter(f => f.id !== fileId);
            const fileToRemove = prev.find(f => f.id === fileId);
            if (fileToRemove?.previewUrl && fileToRemove.file) { // Проверяем file, чтобы не пытаться revokeObjectURL для существующих
                URL.revokeObjectURL(fileToRemove.previewUrl);
            }
            return updated;
        });
        setStatuses(prev => {
            const updated = { ...prev };
            delete updated[fileId];
            return updated;
        });
        setProgress(prev => {
            const updated = { ...prev };
            delete updated[fileId];
            return updated;
        });

        delete abortControllerRef.current[fileId];
        delete lastProgressTimeRef.current[fileId];

        if (activeUploadId === fileId) {
            setActiveUploadId(null);
            processQueueRef.current();
        }
    }, [activeUploadId]);


    // Повторная попытка загрузки
    const retryUpload = useCallback((fileId) => {
        const fileToRetry = uploadedFiles.find(f => f.id === fileId);
        if (!fileToRetry) {
            return;
        }

        if (fileToRetry.status === 'uploading' || fileToRetry.status === 'completed') {
            return;
        }

        // Устанавливаем статус 'pending' и флаг isRetrying
        setUploadedFiles(prev => prev.map(f =>
            f.id === fileId ? { ...f, status: 'pending', progress: 0, isRetrying: true } : f
        ));
        setStatuses(prev => ({ ...prev, [fileId]: 'pending' }));
        setProgress(prev => ({ ...prev, [fileId]: 0 }));


        // Если файл не в очереди, добавляем его обратно
        setQueue(prevQueue => {
            if (!prevQueue.some(f => f.id === fileId)) {
                return [{ ...fileToRetry, status: 'pending', progress: 0, isRetrying: true }, ...prevQueue];
            }
            return prevQueue.map(f => f.id === fileId ? { ...f, status: 'pending', progress: 0, isRetrying: true } : f);
        });

        // Запускаем обработку очереди, если не активна другая загрузка
        if (!activeUploadId) {
            processQueueRef.current();
        }
    }, [uploadedFiles, activeUploadId]);

    const resetUploaderState = useCallback(() => {
        setQueue([]);
        setActiveUploadId(null);
        setProgress({});
        setStatuses({});
        setUploadedFiles([]);
    }, []);

    return {
        files: uploadedFiles,
        queue,
        progress,
        statuses,
        activeUploadId,
        addFiles,
        removeFile,
        retryUpload,
        initializeExistingFiles,
        resetUploaderState,
        isUploading: !!activeUploadId,
        hasPendingUploads: queue.some(f => statuses[f.id] === 'pending' || statuses[f.id] === 'uploading')
    };
};

export default useFileUploader;